import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import useApi, { LoadError } from "@/lib/useApi";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";
import { PageHero, Reveal, DemoTag, Empty } from "@/components/ui-lux";
import { BookmarkButton } from "@/pages/VideoDetail";
import { NotFound } from "@/pages/Misc";

export function Blog() {
  const { data: posts, error, reload } = useApi("/content/posts");
  useSEO({ title: "Free Resources", description: "Articles about faith, mindset, personal development and spiritual growth." });
  return (
    <div data-testid="blog-page">
      <PageHero eyebrow="Free Resources" title="Words for the renewed mind." sub="Articles on faith, mindset, personal development and spiritual growth." />
      <div className="container-x">
        {error ? <LoadError onRetry={reload} /> : posts && !posts.length ? <Empty text="Articles are coming soon." /> : (
          <div className="grid md:grid-cols-3 gap-10">
            {(posts || []).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <Link to={`/blog/${p.slug}`} className="group block" data-testid={`blog-card-${p.slug}`}>
                  <img src={p.cover_image} alt="" className="w-full aspect-[4/3] object-cover rounded-2xl transition-transform duration-700 group-hover:scale-[1.02]" />
                  <div className="flex gap-2 mt-5 items-center"><span className="font-accent text-xs uppercase tracking-wider text-gold-light">{p.category}</span><DemoTag show={p.is_demo} /></div>
                  <h3 className="font-serif text-2xl font-semibold tracking-tight text-white mt-2 group-hover:text-gold-light transition-colors">{p.title}</h3>
                  <p className="text-sm text-slate-400 mt-2">{p.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(undefined);
  const load = () => { setP(undefined); api.get(`/content/posts/${slug}`).then((r) => setP(r.data)).catch((e) => setP(e.response?.status === 404 ? null : "error")); };
  useEffect(load, [slug, user]); // eslint-disable-line react-hooks/exhaustive-deps
  useSEO(p && p !== "error" ? { title: p.seo_title || p.title, description: p.meta_description || p.excerpt, image: p.cover_image,
    jsonLd: { "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, image: p.cover_image, author: { "@type": "Person", name: p.author }, datePublished: p.created_at } } : {});
  if (p === null) return <NotFound />;
  if (p === "error") return <div className="pt-40 container-x"><LoadError onRetry={load} /></div>;
  if (!p) return <div className="min-h-screen" />;
  return (
    <article className="pt-40 container-x max-w-3xl" data-testid="blog-post-page">
      <span className="eyebrow">{p.category} · {fmtDate(p.created_at)}</span>
      <h1 className="h1">{p.title}</h1>
      <p className="text-slate-400 mt-5">By {p.author}</p>
      <img src={p.cover_image} alt="" className="w-full aspect-video object-cover rounded-2xl mt-10" />
      <div className="prose-lux mt-10 text-lg" data-testid="blog-post-body">{p.body.split("\n\n").map((para, i) => (para.startsWith("## ")
        ? <h2 key={i} className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-white mt-12 mb-4">{para.slice(3)}</h2>
        : <p key={i}>{para}</p>))}</div>
      {p.locked && (
        <div className="card-lux p-8 mt-8 text-center" data-testid="post-locked">
          <p className="text-white font-serif text-2xl font-semibold">Continue reading as a member</p>
          <Link to={user ? "/partnership" : "/register"} className="btn-gold mt-5">{user ? "Become a partner" : "Join free"}</Link>
        </div>
      )}
      <div className="mt-10"><BookmarkButton type="post" id={p.id} initial={false} /></div>
    </article>
  );
}
